/**
 * @name lazyload
 * @description 图片懒加载模块。一旦初始化，之后任何时间插入 DOM 的图片，都会具有懒加载行为。
 *    该模块需要配合 CSS 使用，需要在页面头部引入如下 CSS：
 *    <style>
 *      @keyframes imgInserted {
          from {
            opacity: .99;
          }
          to {
            opacity: 1;
          }
        }
        .lazyload, [data-src] {
          animation-duration: .001s;
          animation-name: imgInserted;
        }
      </style>
 * @date 20180616
 * @author lanhai.hy
 */

import MediaTransfer from '@ali/ncom-media-transfer';

//  图片压缩的比例
const COMPRESS_Q = 50;

// 判断当前浏览器是否支持 IntersectionObserver
const isIntersectionObserverSupported = 'IntersectionObserver' in window;

// 判断当前浏览器是否支持 MutationObserver
const isMutationObserverSupported = 'MutationObserver' in window;

// 判断一个元素是不是图片
const isImg = el => typeof el === 'object' && el.tagName && el.tagName.toUpperCase() === 'IMG';

// 获取一个元素的 data-src，若无则返回空字符串
const getDataSrc = el => el.dataset && el.dataset.src || '';

// 获取裁剪的方法
const {
  parseImage,
  parseVideo,
  optimizeImage,
  getImageHeight,
} = MediaTransfer;

// 获取图片 可被裁剪的 尺寸
const getFileSize = src => {

  if (!/_(\d+)x(\d+).([a-z]{3,4})$/.test(src)) return "";

  if (RegExp.$1 >= 720) {
    return 720
  } else if (RegExp.$1 <= 100) {
    return 200
  }
  return "";

}

// 压缩图片的尺寸和兼容webp的格式
const compressSrc = el => {
  let src = getDataSrc(el);

  if (!src) return '';
  if (el.className.indexOf('unCompressImg') >=0 ) return src;

  const fileSize = getFileSize(src);

  // 调用压缩图片方法
  parseImage(src, fileSize, '', fileSize, COMPRESS_Q);

  return src;
}

// 暴露给外部，提前获得压缩后的图片尺寸
const handlerCompressImg = (src) => {
  if (!src) return;

  const fileSize = getFileSize(src);
  
  // 调用压缩图片方法
  return parseImage(src, fileSize, '', fileSize, COMPRESS_Q);

}

// 有 blur 的图片，在高清图加载完成后，去掉 blur
const removeBlur = el => {
  // 有 blur 的图片，在高清图加载完成后，去掉 blur
  if (el.className.indexOf('lazyload-blur') > -1) {
    el.className = el.className.replace('lazyload-blur', '');
  }
}

// 加载图片的 data-src，若无则保留当前 src；非图片元素则设置背景图
const loadImg = el => {
  if (isImg(el)) {
    removeBlur(el);
    // 压缩质量和使用webp格式图片
    let a = compressSrc(el) || el.src
    el.src = a;
  } else {
    el.style.backgroundImage = `url(${compressSrc(el)})`;
  }
}

// 图片懒加载模块
const lazyload = {
  inited: false,
  init() {
    // 避免重复初始化
    if (this.inited) {
      return;
    }
    // 初始化监听器
    if (isIntersectionObserverSupported) {
      // 使用 IntersectionObserver 监听元素，当元素进入可视区域时，加载高清图
      this.intOb = new IntersectionObserver((entries) => {
        for (let entry of entries) {
          // 进入可视区域
          if (entry.intersectionRatio > 0) {
            const el = entry.target;
            this.intOb.unobserve(el);
            loadImg(el);
          }
        }
      }, {
        threshold: [0.01]
      });
    }
    // 监听已存在的元素的 intersection；如果不支持，则直接加载
    [].forEach.call(document.querySelectorAll('[data-src], .lazyload'), el => {
      this.observeIntersection(el);
    });
    // 初始化监听器
    if (isMutationObserverSupported) {
      // 使用 MutationObserver 监听元素的 data-src 属性变化。因为有些元素在异步接口返回后，data-src 才有值。
      this.mutOb = new MutationObserver((mutationList) => {
        mutationList.forEach(((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-src') {
            const el = mutation.target;
            this.observeIntersection(el);
          }
        }));
      });
    }

    // 监听后续插入的元素的 intersection
    document.addEventListener('animationStart', this.onAnimation.bind(this), false);
    document.addEventListener('webkitAnimationStart', this.onAnimation.bind(this), false);

    // 初始化完成
    this.inited = true;
  },
  // 监听后续插入的元素的 intersection
  onAnimation(e) {
    const el = e.target;
    if (e.animationName !== 'imgInserted') {
      return;
    }
    if (getDataSrc(el)) {
      this.observeIntersection(el);
    } else {
      // 如果元素当前 data-src 的值为空，则监听 data-src 属性变化
      this.observeMutation(el);
    }
  },
  observeIntersection(el) {
    if (!el) {
      return;
    }
    // 如果不支持 IntersectionObserver, 则直接加载高清图
    if (!isIntersectionObserverSupported) {
      loadImg(el);
      return;
    }
    // 使用 IntersectionObserver 监听，当图片进入可视区域时，加载高清图
    this.intOb.observe(el);
  },
  observeMutation(el) {
    if (!el) {
      return;
    }
    if (isMutationObserverSupported) {
      this.mutOb.observe(el, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }
  }
}

lazyload.init();

// 把lazyload暴露到全局，允许组件手动监听intersection
// 背景：当页面存在 ai.js 时，这里监听的 animationStart 会失效，
//      导致后续插入 DOM 的元素无法懒加载。因此才把手动监听的能力暴露到全局。
window.__lazyload = lazyload;

// 把压缩图片的方法暴露到全局，方便做图片预加载
export default {
  parseVideo,
  getImageHeight,
  parseImage: handlerCompressImg,
  observe: lazyload.observeIntersection
}
